import { colorFor } from "./palette";

/**
 * KPI tiles, one per group — products sold since 1 July, client services active.
 *
 * A tile carries its share of the total as well as its own figure, because
 * "42 CRS" only means something next to "of 137".
 */
export default function KpiTiles({
  groups = [], total, colors = {}, formatValue, unitLabel, onGroupClick, multiGroupCount = 0, multiGroupRecords = [],
}) {
  const format = formatValue || ((v) => new Intl.NumberFormat("en-GB").format(v));

  if (!groups.length) {
    return <p className="py-12 text-center text-sm text-[#4a5b68]">No records match this report.</p>;
  }

  const denominator = total ?? groups.reduce((sum, g) => sum + g.value, 0);

  return (
    <div>
    <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4" data-testid="kpi-tiles">
      {groups.map((group, index) => {
        const share = denominator ? (group.value / denominator) * 100 : 0;
        return (
          <div
            key={group.key || group.label}
            role={onGroupClick ? "button" : undefined}
            tabIndex={onGroupClick ? 0 : undefined}
            onClick={() => onGroupClick && onGroupClick(group.label)}
            onKeyDown={(event) => {
              if (onGroupClick && (event.key === "Enter" || event.key === " ")) {
                event.preventDefault();
                onGroupClick(group.label);
              }
            }}
            className={`rounded-md border border-[#cccccc] bg-white p-4 ${
              onGroupClick ? "cursor-pointer transition-colors hover:border-[#57c1a9]" : ""
            }`}
            data-testid={`kpi-tile-${group.label}`}
          >
            <div className="flex items-center gap-2">
              <span
                className="h-2.5 w-2.5 flex-shrink-0 rounded-full"
                style={{ backgroundColor: colorFor(group.label, index, colors) }}
              />
              <p className="truncate text-[11px] font-semibold uppercase tracking-[0.16em] text-[#4a5b68]" title={group.label}>
                {group.label}
              </p>
            </div>
            <p className="mt-2 font-sans text-2xl font-bold leading-none text-[#323c44]">
              {format(group.value)}
              {unitLabel ? <span className="ml-1 text-sm font-semibold text-[#4a5b68]">{unitLabel}</span> : null}
            </p>
            <p className="mt-1 text-xs text-[#4a5b68]">
              {share.toFixed(1)}% of {format(denominator)}
            </p>
            {/* How many of them are still live today, when the report asks. */}
            {group.retained !== undefined && group.retained !== null && (
              <p className="mt-1 text-xs text-[#323c44]" data-testid={`retained-${group.label}`}>
                {format(group.retained)} retained of {format(group.count)}
              </p>
            )}
            {/* The share as a bar as well as a figure: it makes the tiles
                comparable at a glance, which a grid of numbers is not. */}
            <div className="mt-3 h-1.5 overflow-hidden rounded-full bg-[#efefef]">
              <div
                className="h-full rounded-full"
                style={{
                  width: `${Math.min(Math.max(share, 0), 100)}%`,
                  backgroundColor: colorFor(group.label, index, colors),
                }}
              />
            </div>
          </div>
        );
      })}
    </div>
    {/* A record on two products is in both tiles, so the tiles add up to
        more than the total. Said out loud, so nobody has to work it out. */}
    {multiGroupCount > 0 && (
      <p className="mt-2 text-xs text-[#4a5b68]" data-testid="kpi-multi-group">
        {format(multiGroupCount)} {multiGroupCount === 1 ? "record is" : "records are"} on more than one
        product, so {multiGroupCount === 1 ? "it is" : "they are"} counted under each and in the total of{" "}
        {format(denominator)}.
        {multiGroupRecords.length > 0 && (
          <>
            {" "}
            {multiGroupRecords.map((record, i) => (
              <span key={record.id}>
                {i > 0 ? "; " : ""}
                <a
                  href={record.url}
                  target="_blank"
                  rel="noreferrer"
                  className="font-semibold text-[#119996] underline"
                  onClick={(event) => event.stopPropagation()}
                >
                  {record.name}
                </a>{" "}
                ({record.groups.join(" and ")})
              </span>
            ))}
            {multiGroupCount > multiGroupRecords.length ? "; and others." : "."}
          </>
        )}
      </p>
    )}
    </div>
  );
}
